export type Optional<T> = T | undefined | null;
export function hasValue<T>(value: Optional<T>): value is T {
  return value != undefined;
}

export function assertValue<T>(value: Optional<T>): T {
  if (!hasValue(value)) {
    throw new Error("Missing value");
  }
  return value;
}

export function nonWhiteSpaceOr<T>(
  value: Optional<string>,
  fallback: T,
): string | T {
  return value?.trim() ? value.trim() : fallback;
}

/**
 * A short identifier suitable for display when no display name is set.
 * Only truncates UUID-shaped ids (hex with hyphens) to their first segment —
 * truncating arbitrary string ids (e.g. the synthetic "dashboard" sender)
 * produced nonsense like "dashboar". Non-UUID ids are returned whole.
 */
function shortId(id: string): string {
  const cleaned = id.trim().replace(/^28:/u, "");
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/iu.test(
    cleaned,
  )
    ? cleaned.slice(0, 8)
    : cleaned;
}

export function displayNameOrShortId(
  displayName: Optional<string>,
  id: string,
): string {
  return nonWhiteSpaceOr(displayName, shortId(id));
}

export function mapValue<T, U>(
  value: Optional<T>,
  callback: (value: T) => U,
): Optional<U> {
  return hasValue(value) ? callback(value) : value;
}

export function isFiniteNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

export function normalizeNonNegativeInteger(value: unknown): number | null {
  if (!isFiniteNumber(value)) {
    return null;
  }
  return Math.max(0, Math.trunc(value));
}

export async function delay(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export function assertUnreachable(x: never): Error {
  // oxlint-disable-next-line typescript/restrict-template-expressions
  throw new Error(`Unexpected: ${x}`);
}

export function hasAny<T>(array: Optional<T[]>): array is T[] {
  return hasValue(array) && array.length > 0;
}

export function first<T>(array: T[]): T {
  if (!array.length) {
    throw new Error("Array is empty");
  }
  return array[0];
}

export function last<T>(array: T[]): T {
  if (!array.length) {
    throw new Error("Array is empty");
  }
  return array[array.length - 1];
}

export function single<T>(array: T[]): T {
  if (!array.length) {
    throw new Error("Array is empty");
  }
  if (array.length > 1) {
    throw new Error("Array has more than one element");
  }
  return array[0];
}

export function keys<TKey extends string>(values: Record<TKey, unknown>) {
  return Object.keys(values).filter((key): key is TKey =>
    Object.hasOwn(values, key),
  );
}

export function stringEnumValues<T extends string>(
  enumObject: Record<string, T>,
): T[] {
  return Object.values(enumObject);
}

export function concat<TA, TB>(a: TA[], ...b: TB[] | TB[][]) {
  const result: (TA | TB)[] = [];
  return result.concat(a).concat(...b);
}

export function sequenceEquals<T>(
  a: T[],
  b: T[],
  predicate: (a: T, b: T) => boolean,
) {
  if (a.length !== b.length) {
    return false;
  }
  for (let i = 0; i < a.length; i += 1) {
    if (!predicate(assertValue(a[i]), assertValue(b[i]))) {
      return false;
    }
  }
  return true;
}

export function sameElementsUnordered<T>(
  a: T[],
  b: T[],
  predicate: (a: T, b: T) => boolean,
) {
  // Order-insensitive multiset equality: every element of `a` must match a
  // distinct element of `b` (and vice versa via the length check). Duplicates
  // are preserved, so ["a", "a"] never equals ["a"].
  if (a.length !== b.length) {
    return false;
  }
  const unmatched = [...b];
  for (const value of a) {
    const index = unmatched.findIndex((candidate) =>
      predicate(assertValue(value), assertValue(candidate)),
    );
    if (index === -1) {
      return false;
    }
    unmatched.splice(index, 1);
  }
  return true;
}

export function sameElementsAsSet<T>(
  a: readonly T[],
  b: readonly T[],
  predicate: (a: T, b: T) => boolean,
) {
  // True set equality: order-insensitive AND duplicate-insensitive. Unlike
  // sameElementsUnordered (multiset semantics), ["a", "a"] equals ["a"].
  const deduplicated = (values: readonly T[]): T[] =>
    values.filter(
      (value, index) =>
        values.findIndex((candidate) =>
          predicate(assertValue(value), assertValue(candidate)),
        ) === index,
    );
  const aSet = deduplicated(a);
  const bSet = deduplicated(b);
  if (aSet.length !== bSet.length) {
    return false;
  }
  const unmatched = [...bSet];
  for (const value of aSet) {
    const index = unmatched.findIndex((candidate) =>
      predicate(assertValue(value), assertValue(candidate)),
    );
    if (index === -1) {
      return false;
    }
    unmatched.splice(index, 1);
  }
  return true;
}

export function chunkify<T>(array: T[], chunkSize: number): T[][] {
  const chunks: T[][] = [];
  for (let i = 0; i < array.length; i += chunkSize) {
    chunks.push(array.slice(i, i + chunkSize));
  }
  return chunks;
}

export function chunkifyByWeight<T>(
  array: T[],
  weight: (value: T) => number,
  chunkWeight: number,
) {
  const chunks: T[][] = [];
  let currentChunk: T[] = [];
  let currentWeight = 0;
  for (const value of array) {
    const valueWeight = weight(value);
    if (currentWeight + valueWeight <= chunkWeight) {
      currentChunk.push(value);
      currentWeight += valueWeight;
    } else {
      chunks.push(currentChunk);
      currentChunk = [value];
      currentWeight = valueWeight;
    }
  }

  if (hasAny(currentChunk)) {
    chunks.push(currentChunk);
  }

  return chunks;
}

export function mapAsync<T, U>(
  array: T[],
  callback: (value: T, index: number, array: T[]) => Promise<U>,
) {
  return Promise.all(array.map(callback));
}

export async function mapAsyncSerial<T, U>(
  array: T[],
  callback: (value: T, index: number, array: T[]) => Promise<U>,
) {
  const results: U[] = [];
  for (const [i, entry] of array.entries()) {
    results.push(await callback(entry, i, array));
  }
  return results;
}

export function upsertByKey<TValue, TKey>(
  array: TValue[],
  key: TKey,
  getKey: (value: TValue) => TKey,
  value: (previous: Optional<TValue>) => TValue,
) {
  array = array.slice();
  const index = array.findIndex((item) => getKey(item) === key);
  if (index !== -1) {
    array[index] = value(array[index]);
  } else {
    array.push(value(null));
  }
  return array;
}

export function filterAsync<T>(
  array: T[],
  callback: (value: T, index: number, array: T[]) => Promise<boolean>,
) {
  return Promise.all(array.map(callback)).then((results) =>
    array.filter((_, index) => results[index]),
  );
}

export function mapSum<T>(
  array: T[],
  callback: (value: T, index: number, array: T[]) => number,
) {
  return array.reduce(
    (sum, value, index) => sum + callback(value, index, array),
    0,
  );
}

export async function* buffer<T>(
  iterator: AsyncIterableIterator<T>,
  size = 2,
): AsyncIterableIterator<T> {
  const queue = [];
  for (let i = 0; i < size; i++) {
    queue.push(iterator.next());
  }
  while (true) {
    const current = assertValue(queue.shift());
    queue.push(iterator.next());

    const result = await current;
    if (result.done) {
      break;
    }

    yield result.value;
  }
}

type MinusOne = [-1, 0, 1, 2, 3, 4, 5, 6, 7, 8, 9];
type NestedArray<T, D extends number> = D extends 0
  ? T[]
  : T[] | NestedArray<T[], MinusOne[D]>;

export function isFlat<T>(array: NestedArray<T, 10>): array is T[] {
  return !array.some(Array.isArray);
}

export function generateId() {
  const chars = "abcdefghijklmnopqrstuvwxyz0123456789";
  return Array.from(
    { length: 10 },
    () => chars[Math.floor(Math.random() * chars.length)],
  ).join("");
}

export function stringHash(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (hash << 5) - hash + str.charCodeAt(i);
    hash |= 0;
  }
  return hash;
}

export function parseJson(json: string): Optional<unknown> {
  try {
    return JSON.parse(json);
  } catch {
    return null;
  }
}

export function parseBase64Json(base64: string): Optional<unknown> {
  try {
    const json = atob(base64);
    return JSON.parse(json);
  } catch {
    return null;
  }
}

export function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value && typeof value === "object" && !Array.isArray(value));
}

export function asRecord(value: unknown): Record<string, unknown> | null {
  return isRecord(value) ? value : null;
}

export function readStringProperty(
  record: Record<string, unknown>,
  key: string,
): string | undefined {
  const value = record[key];
  return typeof value === "string" ? value : undefined;
}

export function isOneOf<TValue extends string>(
  value: string,
  allowed: readonly TValue[],
): value is TValue {
  return allowed.some((entry) => entry === value);
}

export function getErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof error === "string") {
    return error;
  }
  return String(error);
}
